import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, watch } from 'vue'

import type { ConfigurationResult } from '@/domains/configuration/types/Configuration.types'
import type { UseEntityQueryOptions } from '@/support/query/UseEntityQueryOptions.types'

import { CONFIGURATION_QUERY_KEY } from '@/domains/configuration/composables/use-configuration-query-keys/useConfigurationQueryKeys'
import { callConfiguration } from '@/domains/configuration/services'
import { CONFIGURATION_COPY } from '@/domains/configuration/utils/i18n/copy'
import { useToastStore } from '@/domains/notifications/store/use-toast-store/useToastStore'
import { GC_TIMES, STALE_TIMES } from '@/support/query/constants'

/**
 * Cross-domain read-only access to the configuration registry.
 *
 * Reads from the shared TanStack cache under `CONFIGURATION_QUERY_KEY`. The
 * cache is normally seeded by `useHydrateConfigurationCache` on a page that
 * loads `ConfigurationStructure` via Inertia (currently only the admin
 * configuration page). If the cache is cold, this composable falls back to
 * a fresh `callConfiguration().index()` request.
 *
 * Consumers in other domains (e.g. quick-updates, bills) should expect a
 * brief loading state on first read unless their parent page has already
 * hydrated.
 */
export const useConfigurationQuery = (options: UseEntityQueryOptions = {}) => {
  const toastStore = useToastStore()

  const query = useQuery({
    queryKey: [...CONFIGURATION_QUERY_KEY],
    queryFn: async () => {
      const response = await callConfiguration().index()
      return response.data.value?.current_values ?? {}
    },
    enabled: computed(() => toValue(options.enabled) ?? true),
    staleTime: STALE_TIMES.STATIC,
    gcTime: GC_TIMES.EXTENDED
  })

  watch(query.isError, hasError => {
    if (!hasError) return
    toastStore.add({
      state: 'once',
      severity: 'error',
      summary: CONFIGURATION_COPY.mutations.loadError.summary,
      detail: CONFIGURATION_COPY.mutations.loadError.detail
    })
  })

  function get<T>(key: string, fallback: T): T
  function get<T = unknown>(key: string): T | undefined
  function get<T = unknown>(key: string, fallback?: T): T | undefined {
    const entry = query.data.value?.[key] as ConfigurationResult | undefined
    return (entry?.value as T | undefined) ?? fallback
  }

  return {
    ...query,
    get
  }
}
